import { useEffect, useMemo, useState } from "react";
import { Copy, Eye, FileText, Link2, Loader2, Search, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, DOMAINES, domaineLabel, fmtDate } from "@/lib/api";
import DocumentViewer from "@/components/DocumentViewer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const CATEGORIES = [
  { key: "technique", label: "Documents techniques (matériel)" },
  { key: "administratif", label: "Documents administratifs (chantier)" },
];

export default function AdminDocuments() {
  const [categorie, setCategorie] = useState("technique");
  const [q, setQ] = useState("");
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState(null);
  const [contact, setContact] = useState(null);
  const [docs, setDocs] = useState([]);
  const [shares, setShares] = useState([]);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ titre: "", description: "", site_id: "", domaine: "", materiel: "", visible_client: true });
  const [share, setShare] = useState({ email: "", jours: 30, libelle: "" });
  const [apercu, setApercu] = useState(null);
  const docsCategorie = useMemo(() => docs.filter((d) => d.categorie === categorie), [docs, categorie]);
  const [busy, setBusy] = useState(false);

  const loadDocs = (axonautId) => api.get("/admin/documents", { params: { axonaut_id: axonautId } }).then(({ data }) => setDocs(data));
  const loadShares = () => api.get("/admin/documents/shares").then(({ data }) => setShares(data));

  useEffect(() => {
    loadShares();
  }, []);

  const search = async () => {
    const { data } = await api.get("/admin/clients/search", { params: { q } });
    setClients(data);
    if (!data.length) toast.error("Aucun client trouvé");
  };

  const select = async (c) => {
    setClient(c);
    const { data } = await api.get(`/admin/clients/${c.axonaut_id}`);
    setContact(data);
    setForm((f) => ({ ...f, site_id: data.sites?.[0]?.id || "" }));
    loadDocs(c.axonaut_id);
  };

  const upload = async () => {
    if (!file) return toast.error("Choisissez un fichier");
    setBusy(true);
    const site = contact?.sites?.find((s) => s.id === form.site_id);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("categorie", categorie);
    fd.append("titre", form.titre || file.name);
    fd.append("description", form.description);
    fd.append("axonaut_id", client.axonaut_id);
    fd.append("site_id", form.site_id);
    fd.append("site_nom", site?.nom || "");
    fd.append("domaine", form.domaine);
    fd.append("materiel", form.materiel);
    fd.append("visible_client", form.visible_client);
    try {
      await api.post("/admin/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document déposé");
      setFile(null);
      setForm({ ...form, titre: "", description: "", materiel: "" });
      loadDocs(client.axonaut_id);
    } catch (e) {
      toast.error(errMsg(e, "Dépôt impossible"));
    } finally {
      setBusy(false);
    }
  };

  const createShare = async () => {
    const site = contact?.sites?.find((s) => s.id === form.site_id);
    const { data } = await api.post("/admin/documents/share", {
      axonaut_id: client.axonaut_id,
      site_id: form.site_id || null,
      site_nom: site?.nom || contact?.nom,
      email: share.email || null,
      jours: Number(share.jours) || 30,
      libelle: share.libelle,
    });
    await navigator.clipboard?.writeText(data.lien).catch(() => {});
    toast.success(share.email ? "Invitation envoyée et lien copié" : "Lien de partage copié");
    setShare({ email: "", jours: 30, libelle: "" });
    loadShares();
  };

  return (
    <div className="space-y-6" data-testid="admin-documents">
      <div>
        <div className="bk-label mb-2">Coffre-fort</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900">Documents client</h1>
        <p className="text-sm text-slate-600 mt-2">
          Déposez les notices et documents techniques liés au matériel posé, ou les documents administratifs d'un chantier (notes de calcul,
          attestations, plans). Les fichiers sont stockés de façon privée : seuls le client concerné, l'équipe BKPRO et les personnes invitées
          peuvent les ouvrir.
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            data-testid={`doc-cat-${c.key}`}
            onClick={() => setCategorie(c.key)}
            className={`text-sm px-4 py-2 rounded-full border transition-colors ${categorie === c.key ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:border-slate-500"}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <Input data-testid="doc-client-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Rechercher un client" className="h-12" />
        <Button data-testid="doc-client-search-btn" onClick={search} className="h-12 bg-slate-900 hover:bg-slate-700">
          <Search size={16} />
        </Button>
      </div>

      {clients.length > 0 && !client && (
        <div className="space-y-2">
          {clients.map((c) => (
            <button key={c.axonaut_id} data-testid={`doc-client-${c.axonaut_id}`} onClick={() => select(c)} className="w-full text-left rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-[#005CFF]">
              <div className="text-sm font-semibold text-slate-900">{c.nom}</div>
              <div className="text-xs text-slate-500">{c.source} · #{c.axonaut_id}</div>
            </button>
          ))}
        </div>
      )}

      {client && contact && (
        <div className="space-y-6" data-testid="doc-client-panel">
          <div className="flex items-center justify-between">
            <div className="font-display text-xl font-bold text-slate-900">{contact.nom}</div>
            <Button variant="outline" onClick={() => { setClient(null); setContact(null); setDocs([]); }}>Changer de client</Button>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
              <div className="bk-label">Déposer un document</div>
              <input data-testid="doc-file-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm text-slate-600" />
              <Input data-testid="doc-titre-input" value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} placeholder="Titre du document" className="h-11" />
              <Textarea data-testid="doc-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Description (optionnel)" />
              <select data-testid="doc-site-select" value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} className="w-full h-11 border border-slate-200 rounded-lg px-3 text-sm">
                <option value="">Tous les sites</option>
                {contact.sites?.map((s) => (
                  <option key={s.id} value={s.id}>{s.nom}</option>
                ))}
              </select>
              {categorie === "technique" && (
                <>
                  <select data-testid="doc-domaine-select" value={form.domaine} onChange={(e) => setForm({ ...form, domaine: e.target.value })} className="w-full h-11 border border-slate-200 rounded-lg px-3 text-sm">
                    <option value="">Domaine (optionnel)</option>
                    {DOMAINES.map((d) => (
                      <option key={d.key} value={d.key}>{d.label}</option>
                    ))}
                  </select>
                  <select data-testid="doc-materiel-select" value={form.materiel} onChange={(e) => setForm({ ...form, materiel: e.target.value })} className="w-full h-11 border border-slate-200 rounded-lg px-3 text-sm">
                    <option value="">Matériel concerné (optionnel)</option>
                    {contact.materiel?.map((m, i) => (
                      <option key={i} value={m.designation}>{m.designation}</option>
                    ))}
                  </select>
                </>
              )}
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <Checkbox data-testid="doc-visible-client" checked={form.visible_client} onCheckedChange={(v) => setForm({ ...form, visible_client: !!v })} /> Visible par le client
              </label>
              <Button data-testid="doc-upload-btn" onClick={upload} disabled={busy} className="bg-[#005CFF] hover:bg-[#0047CC]">
                {busy ? <Loader2 size={15} className="animate-spin mr-2" /> : <Upload size={15} className="mr-2" />} Déposer
              </Button>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
              <div className="bk-label">Inviter un tiers / lien temporaire (documents administratifs)</div>
              <p className="text-xs text-slate-500">
                Le lien donne accès en lecture aux documents administratifs du chantier sélectionné, sans compte. Il expire automatiquement et
                reste révocable.
              </p>
              <Input data-testid="share-email-input" value={share.email} onChange={(e) => setShare({ ...share, email: e.target.value })} placeholder="Email de l'architecte / tiers (optionnel)" className="h-11" />
              <Input data-testid="share-libelle-input" value={share.libelle} onChange={(e) => setShare({ ...share, libelle: e.target.value })} placeholder="Intitulé (ex : Architecte — lot électricité)" className="h-11" />
              <select data-testid="share-jours-select" value={share.jours} onChange={(e) => setShare({ ...share, jours: e.target.value })} className="w-full h-11 border border-slate-200 rounded-lg px-3 text-sm">
                <option value={7}>Expire dans 7 jours</option>
                <option value={30}>Expire dans 30 jours</option>
                <option value={90}>Expire dans 90 jours</option>
              </select>
              <Button data-testid="share-create-btn" onClick={createShare} variant="outline">
                <Link2 size={15} className="mr-2" /> Créer le lien sécurisé
              </Button>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="bk-label mb-3">Documents du client ({docsCategorie.length})</div>
            <div className="space-y-2">
              {docsCategorie.map((d) => (
                <div key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2">
                      <FileText size={14} /> {d.titre}
                    </div>
                    <div className="text-xs text-slate-500">
                      {d.site_nom || "tous sites"} {d.materiel ? `· ${d.materiel}` : ""} {d.domaine ? `· ${domaineLabel(d.domaine)}` : ""} ·{" "}
                      {Math.round((d.size || 0) / 1024)} Ko · {fmtDate(d.created_at)} {d.visible_client ? "" : "· masqué au client"}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {d.vues || 0} accès {d.dernier_acces ? `· dernier le ${fmtDate(d.dernier_acces)}` : "· jamais consulté"}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {d.previewable && (
                      <button data-testid={`doc-preview-${d.id}`} onClick={() => setApercu(d)} title="Aperçu" className="p-2 text-slate-400 hover:text-[#005CFF]">
                        <Eye size={15} />
                      </button>
                    )}
                    <button
                      data-testid={`doc-delete-${d.id}`}
                      onClick={async () => {
                        await api.delete(`/admin/documents/${d.id}`);
                        loadDocs(client.axonaut_id);
                      }}
                      className="text-slate-300 hover:text-red-500"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
              {docsCategorie.length === 0 && <p className="text-sm text-slate-500">Aucun document dans cette catégorie.</p>}
            </div>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="shares-list">
        <div className="bk-label mb-3">Accès partagés ({shares.length})</div>
        <div className="space-y-2">
          {shares.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2 text-sm">
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">
                  {s.libelle || s.email || "Lien de partage"} {s.revoked && <span className="text-red-600 text-xs">(révoqué)</span>}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {s.site_nom || "tous chantiers"} · expire {fmtDate(s.expires_at)} · {s.acces || 0} ouverture(s)
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => { navigator.clipboard?.writeText(s.lien); toast.success("Lien copié"); }} className="p-2 text-slate-400 hover:text-[#005CFF]" title="Copier le lien">
                  <Copy size={14} />
                </button>
                {!s.revoked && (
                  <button
                    data-testid={`share-revoke-${s.id}`}
                    onClick={async () => {
                      await api.delete(`/admin/documents/shares/${s.id}`);
                      loadShares();
                      toast.success("Accès révoqué");
                    }}
                    className="p-2 text-slate-300 hover:text-red-500"
                    title="Révoquer"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
          ))}
          {shares.length === 0 && <p className="text-sm text-slate-500">Aucun accès partagé.</p>}
        </div>
      </div>

      {apercu && <DocumentViewer doc={apercu} url={`/admin/documents/${apercu.id}/download`} onClose={() => setApercu(null)} />}
    </div>
  );
}
